import {Trans} from '@lingui/macro';
import {ellipsis, transparentize} from 'polished';
import React, {FC, useCallback, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isInteger} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {buildTeammateShortDisplayName} from '../../../helpers/modelHelpers';
import {isHighlightComment} from '../../../helpers/models/commentHelpers';
import {AttachmentModel} from '../../../models/api/attachmentModel';
import {CommentModel} from '../../../models/api/commentModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {NewPanelId} from '../../../store/panels/panelStateBase';
import {CommentBody} from '../../inboxes/viewer/activity/comment/commentBody';
import {TeammateAvatar} from '../avatars/teammateAvatar';
import {InteractiveDateTimeFormatterContainer} from '../formatters/dateTime/interactiveDateTimeFormatterContainer';
import {GridAreaItem} from '../grid';

interface FloatingCommentDropdownProps extends DropdownProps {
  panelId: NewPanelId;
  comment: CommentModel;
  userId: number;
  author?: TeammateModel;
  attachments: ReadonlyArray<AttachmentModel>;
  allAliases: ReadonlySet<string>;
  mentionableAliases: ReadonlySet<string>;
  userAlias: string;
  hasQuoteComment: boolean;
  onJumpCommentClick: Handler;
  conversationId?: number;
  namespace?: string;
}

/*
 * Styles.
 */

const StyledDropdown = styled(Dropdown)<{$hasHover?: boolean}>`
  padding: 10px;
  min-width: 300px;
  border-radius: 12px;

  ${(p) =>
    p.$hasHover &&
    css`
      &:hover {
        background-color: ${p.theme.greys.shade10};
      }
    `}
`;

const StyledHeaderContentDiv = styled.div`
  display: grid;
  grid-template-areas: 'avatar . text . timestamp';
  grid-template-columns: auto 6px 1fr 16px auto;
  align-items: center;
  position: relative;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

const StyledHeaderHoverLabel = styled.div`
  z-index: 1;
  opacity: 0;
  transition: opacity 300ms ease;
  color: ${(p) => p.theme.palette.blue.shade40};
  background-color: ${(p) => p.theme.greys.shade10};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;

  ${StyledHeaderContentDiv}:hover & {
    opacity: 1;
  }
`;

const StyledAuthorDiv = styled.div`
  grid-area: text;
  margin-top: -1px;
  ${ellipsis()}
`;

const StyledTimestampDiv = styled.div`
  grid-area: timestamp;
  display: flex;
  justify-content: flex-end;
  text-align: right;

  color: ${(p) => p.theme.greys.shade60};
  text-transform: uppercase;

  ${ellipsis()}
`;

const StyledCommentBody = styled.div`
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 7px;
  margin-top: 8px;
  position: relative;

  overflow: hidden;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

const StyledCommentBodyHover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: center;
  background: linear-gradient(
    180deg,
    ${(p) => transparentize(0.4, p.theme.greys.shade30)} 0%,
    ${(p) => p.theme.greys.shade30} 100%
  );
  z-index: 1;
  opacity: 0;
  transition: opacity 300ms ease;

  ${StyledCommentBody}:hover & {
    opacity: 1;
  }
`;

const StyledCommentBodyHoverLabel = styled.div`
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${(p) => p.theme.palette.blue.shade40};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
`;

/*
 * Component.
 */

export const FloatingCommentDropdown: FC<FloatingCommentDropdownProps> = (props) => {
  const {comment, author, onRequestClose, anchor, userId, namespace, conversationId} = props;
  const isHighlight = isHighlightComment(comment);
  const shouldTrackActions = useMemo(
    () => isInteger(conversationId) && namespace,
    [conversationId, namespace],
  );
  const maybeTrackJumpToComment = useCallback(() => {
    if (!shouldTrackActions) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_COMMENT_CLICK, {
      namespace,
      conversation_id: conversationId,
      cta_type: 'jump_to_comment',
      location: 'p3_window',
      has_comment_preview: !isHighlight,
    });
  }, [conversationId, isHighlight, namespace, shouldTrackActions]);

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_COMMENT_PREVIEW, {
      namespace,
      conversation_id: conversationId,
      has_comment_preview: !isHighlight,
    });
  });

  if (!author) {
    return null;
  }

  const isUserTheAuthor = author.id === userId;
  const hasAnnotation = Boolean(comment.annotation);

  const onHeaderClick = isHighlight
    ? () => {
        props.onJumpCommentClick();
        props.onRequestClose?.();
        maybeTrackJumpToComment();
      }
    : undefined;

  return (
    <StyledDropdown
      width={336}
      maxHeight={400}
      anchor={anchor}
      onRequestClose={onRequestClose}
      $hasHover={isHighlight}
      isExclusive={false}
      isInline
    >
      <StyledHeaderContentDiv onClick={onHeaderClick}>
        {isHighlight && (
          <StyledHeaderHoverLabel>
            <Icon name="arrowThickSmall" />
            <Trans id="floating-comment-jump">Jump</Trans>
          </StyledHeaderHoverLabel>
        )}
        <GridAreaItem area="avatar">
          <TeammateAvatar teammate={author} size={VisualSizesEnum.MEDIUM} />
        </GridAreaItem>
        <StyledAuthorDiv>{renderTitle(author, isUserTheAuthor, isHighlight)}</StyledAuthorDiv>
        <StyledTimestampDiv>
          <InteractiveDateTimeFormatterContainer value={comment.sentAt} />
        </StyledTimestampDiv>
      </StyledHeaderContentDiv>
      {!isHighlight && (
        <StyledCommentBody>
          {hasAnnotation && (
            <StyledCommentBodyHover
              onClick={() => {
                props.onJumpCommentClick();
                props.onRequestClose?.();
                maybeTrackJumpToComment();
              }}
            >
              <StyledCommentBodyHoverLabel>
                <Icon name="arrowThickSmall" />
                <Trans id="floating-comment-jumptocomment">Jump to comment</Trans>
              </StyledCommentBodyHoverLabel>
            </StyledCommentBodyHover>
          )}
          <CommentBody
            comment={comment}
            allAliases={props.allAliases}
            mentionableAliases={props.mentionableAliases}
            userAlias={props.userAlias}
            attachments={props.attachments}
            hasQuoteComment={props.hasQuoteComment}
            panelId={props.panelId}
            shouldHideAnnotation
            namespace={undefined}
          />
        </StyledCommentBody>
      )}
    </StyledDropdown>
  );
};

function renderTitle(author: TeammateModel, isUserTheAuthor: boolean, isHighlight: boolean) {
  if (isHighlight) {
    if (isUserTheAuthor) {
      return (
        <Trans id="floating-comment-author-you">
          <strong>You</strong> added this in a comment
        </Trans>
      );
    }

    return (
      <Trans id="floating-comment-author-highlighted">
        <strong>{buildTeammateShortDisplayName(author)}</strong> added this in a comment
      </Trans>
    );
  }

  if (isUserTheAuthor) {
    return (
      <Trans id="floating-comment-author-you-commented">
        <strong>You</strong> commented
      </Trans>
    );
  }

  return (
    <Trans id="floating-comment-author-commented">
      <strong>{buildTeammateShortDisplayName(author)}</strong> commented
    </Trans>
  );
}
